import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import SEO from "@/components/site/SEO";
import { popConfetti } from "@/lib/confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, source: "contact-page" });
      toast.success("Message received. We'll be in touch within one business day.");
      popConfetti({ x: 0.5, y: 0.3 });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <SEO title="Contact" description="Talk to SECQRE. Sales, support, partnerships, and fleet inquiries." path="/contact" />

      <section className="pt-32 md:pt-40 pb-16">
        <div className="container-secqre grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline">— Contact</span>
            <h1 className="mt-4 font-heading font-black text-5xl lg:text-6xl tracking-tight" data-testid="contact-title">
              Let&apos;s talk privacy.
            </h1>
            <p className="mt-6 text-lg text-[#475569]">
              Fleet inquiry? Reseller program? Partnership? Just curious how the masked call works?
              We answer everything within one business day.
            </p>

            <div className="mt-10 space-y-5">
              <ContactItem icon={Mail} label="Email" value="hello@secqre.com" />
              <ContactItem icon={Phone} label="Phone" value="+1 (415) 555 0173" />
              <ContactItem icon={MapPin} label="HQ" value="San Francisco, CA · United States" />
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 rounded-2xl border border-[#E2E8F0] bg-white p-8 md:p-10" data-testid="contact-form">
            <h2 className="font-heading font-bold text-2xl mb-6">Send us a message</h2>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Full name" required>
                <input required value={form.name} onChange={update("name")} className="form-input" data-testid="contact-input-name" />
              </Field>
              <Field label="Email" required>
                <input type="email" required value={form.email} onChange={update("email")} className="form-input" data-testid="contact-input-email" />
              </Field>
              <Field label="Phone (optional)">
                <input value={form.phone} onChange={update("phone")} className="form-input" data-testid="contact-input-phone" />
              </Field>
              <Field label="Subject">
                <input value={form.subject} onChange={update("subject")} placeholder="Fleet inquiry, reseller, support..." className="form-input" data-testid="contact-input-subject" />
              </Field>
            </div>
            <Field label="Message" required className="mt-5">
              <textarea
                required
                rows={6}
                value={form.message}
                onChange={update("message")}
                className="form-input"
                placeholder="Tell us about your use case..."
                data-testid="contact-input-message"
              />
            </Field>

            <button type="submit" disabled={loading} className="btn-primary mt-7" data-testid="contact-submit">
              {loading ? "Sending..." : (<>Send message <Send className="w-4 h-4" /></>)}
            </button>
            <p className="mt-4 text-xs text-[#94a3b8]">We never share your details. Same promise as our masked calls.</p>

            <style>{`
              .form-input {
                width: 100%;
                padding: 0.75rem 1rem;
                border-radius: 0.5rem;
                border: 1px solid #E2E8F0;
                background: #ffffff;
                color: #0A0A0A;
                font-size: 0.95rem;
                outline: none;
                transition: all 0.2s;
              }
              .form-input:focus {
                border-color: #0F172A;
                box-shadow: 0 0 0 3px rgba(15,23,42,0.08);
              }
            `}</style>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-semibold text-[#0A0A0A] mb-2">
        {label} {required && <span className="text-[#E63946]">*</span>}
      </span>
      {children}
    </label>
  );
}

function ContactItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-lg bg-[#F8F9FA] border border-[#E2E8F0] inline-flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#0F172A]" />
      </div>
      <div>
        <div className="text-xs text-[#475569] uppercase tracking-wider font-semibold">{label}</div>
        <div className="text-[#0A0A0A] font-medium mt-0.5">{value}</div>
      </div>
    </div>
  );
}
